
define((require) => {
    "use strict";

    const $                         = require("jquery");
    const Class                     = require("shared/util/Class");
    const HttpStatusCodes           = require("shared/util/HttpStatusCodes");
    const ErrorHandler              = require("shared/services/error/ErrorHandler");
    const SyncManager               = require("shared/services/sync/SyncManager");
    const Preconditions             = require("shared/util/Preconditions");
    const FileSyncManagerValidator  = require("validators/FileSyncManagerValidator");
    const TextUtil                  = require("shared/util/TextUtil");
    const Permissions               = require("shared/security/Permissions");
    const Promise                   = require("shared/util/Promise");
    const Storage                   = require("shared/services/Storage");

    const TYPE = "upload";
    const filenameToFileSizeMap = {};

    /**
     * An abstract file storage service used to upload file in Creator. Currently used only for image upload
     *
     * @class AbstractFileStorage
     *
     * @see ImageFileStorage
     * @see CustomFontFileStorage
     */
    const AbstractFileStorage = Class.extend("AbstractFileStorage", {

        isSameFileBeingUploaded(file) {
            return filenameToFileSizeMap[file.name] === file.size;
        },

        /**
         * Stores the given file. <p>
         *
         * When the file is uploaded, the returned promise will resolve to the right model. <p>
         *
         * If the file is a duplicate of an existing file, it will be handle according to the file, byeither returning
         * existing model or throwing an error.
         *
         * @param   {File}    file The file to store; cannot be falsey.
         * @returns {Promise} A promise that will be resolved with the file model when the file has been stored or
         *                    rejected, for example because of an illegal file format.
         */
        storeFile(file) {
            const uploadImageOnC3 = () => {
                const token = Storage.Local.get(Storage.Keys.LOGIN, "token") || Storage.Session.get(Storage.Keys.LOGIN, "token");
                return Promise.deferred(deferred => {
                    fetch(
                        modelInfo.url,
                        {
                            body: file,
                            headers: {
                                Authorization: `Bearer ${token}`,
                            },
                            method: "POST",
                        })
                        .then(res =>  {
                            if (res.status === HttpStatusCodes.CONFLICT || res.status === HttpStatusCodes.OK) {
                                return res.json();
                            }
                        })
                        .then(data => {
                            const imgData = data.imageDetails || data;
                            ErrorHandler.discard({ tag: `${TYPE}:` }); // all such errors
                            const newImgData = Object.assign({}, imgData, { id: imgData.imageId });
                            return modelClass.findOrCreate(newImgData);
                        })
                        .then(model => deferred.resolve(model))
                }) 

            }

            const legacyUpload = () => {
                const fileData = new FormData();
                fileData.append("file", file);
                // We have to call ajax explicitly here, since the upload is two-fold: first the upload,
                // the the file resolving, which contexts will be listening for...
                return $.ajax({
                    type: "POST",
                    // The filename is included as a query parameter because of problems with resteasy: https://issues.jboss.org/browse/RESTEASY-390
                    // and https://issues.jboss.org/browse/RESTEASY-305 and https://jira.zmags.com/browse/MOS-2325.
                    url: modelInfo.url,
                    data: fileData,
                    xhr() {
                        const myXhr = $.ajaxSettings.xhr();
                        if (myXhr.upload) {
                            myXhr.upload.addEventListener("progress", (event) => {
                                deferred.notify(event.loaded / event.total);
                            }, false);
                        }
                        return myXhr;
                    },
                    processData: false,
                    contentType: false
                }).then((data) => {
                    // Make sure we discard any pending error if we make a successful upload
                    ErrorHandler.discard({ tag: `${TYPE}:` }); // all such errors
                    return modelClass.findOrCreate(data);
                });
            };
            // Create a fake target based on the file to get a queue for, since we want the exact promise
            // returned so we get obtain the deferred to notify upload progress on...
            const target = SyncManager.target(file.name);

            let deferred; // initialised below, based on "promise"...
            const queue = SyncManager.queue(target);
            const modelInfo = this._modelInfo(file);

            const modelClass = modelInfo.modelClass;
            const supportedTypes = modelInfo.supportedTypes;

            const promise = queue.enqueue({ // request promise, not overall queue promise!
                method: TYPE,
                role: Permissions.Roles.CREATOR,

                fn() {
                    if (modelInfo.c3ApiUpload) {
                        return uploadImageOnC3();
                    }
                    else return legacyUpload();
                },

                validate: () => this._validate(file, supportedTypes),

                resultHandler: (model) => model,

                errorHandler: (error, options) => this._handleError(file, error, options, modelInfo)
            });

            // Get the deferred backing the queue entry so we can notify against it...
            deferred = promise.entry.request.deferred();

            // Start upload!..
            queue.sync();

            // Make sure clean up before given our promise...
            return promise.always(() => {
                // Remove the file from the pending uploads...
                if (filenameToFileSizeMap[file.name] === file.size) {
                    delete filenameToFileSizeMap[file.name];
                }
            });
        },

        _validate(file, supportedTypes) {
            const isSameFileBeingUploaded = this.isSameFileBeingUploaded(file);

            const status = FileSyncManagerValidator.validate(file, isSameFileBeingUploaded, supportedTypes);

            if (status !== HttpStatusCodes.OK) {
                return status;
            }

            filenameToFileSizeMap[file.name] = file.size;
        },

        // Error handler to catch unsupported file types and size
        _handleError(file, error, options, { errorMessages, supportedTypes }) {
            let fileName = `&quot;${file.name}&quot;`;
            const hasErrorMessage = Boolean(errorMessages);

            switch (error.status) {
                case HttpStatusCodes.CONFLICT:
                    error.message = (hasErrorMessage && errorMessages.CONFLICT) || "the file has already been uploaded.";
                    error.action = ""; // Remove the "retry" option
                    break;

                case HttpStatusCodes.LENGTH_REQUIRED:
                    error.message = (hasErrorMessage && errorMessages.LENGTH_REQUIRED) || "the file provided is not valid.";
                    error.action = "community"; // Will show associated Community article
                    fileName = file.name;
                    break;

                case HttpStatusCodes.REQUEST_ENTITY_TOO_LARGE:
                    error.message = (hasErrorMessage && errorMessages.REQUEST_ENTITY_TOO_LARGE) || "the file size is too large. Please try again with a smaller file.";
                    break;

                case HttpStatusCodes.UNSUPPORTED_MEDIA_TYPE: // unsupported media type
                    error.message = (hasErrorMessage && errorMessages.REQUEST_ENTITY_TOO_LARGE) || `the file type is not supported. Please try again with a supported type: ${TextUtil.list(supportedTypes, ", ", ", or ")}`;
                    break;

                default:
                    break;
            }

            error.code = ErrorHandler.Codes.UPLOAD; // for community

            // Resolve error message placeholders
            ErrorHandler.inject(error, {
                method:  TYPE,
                name:    fileName
            });

            options.tag = `${TYPE}.${error.status}`;
            options.closeDelay = 10000; // do not leave them hangin'

            return "alert";
        },

        // eslint-disable-next-line no-unused-vars
        _modelInfo(file) {
            Preconditions.abstractMethodError();
        }

    });

    return AbstractFileStorage;
});



// WEBPACK FOOTER //
// ./src/editor/services/files/AbstractFileStorage.js